/**
 * create_line_items_controller.js — the line-item breakdown of a create form (Stimulus).
 *
 * Hosts the shared LineItemsManager (static/js/utils/line-items-manager.js) for
 * the create forms that have a breakdown: the quote form and the job form. The
 * markup is templates/macros/create_line_items.html, which puts this controller
 * on the section holding the toggle, the rows and the totals, and gives every id
 * a stem so two forms can share a page (the dashboard renders both). This
 * controller derives the same ids from the same stem, because the manager finds
 * its elements by id: the Price input and its locked indicator and help texts
 * sit elsewhere in the form, outside this element.
 *
 * Config, all data-create-line-items-*-value on the section: stem, fn-prefix
 * (suffix of the manager's global row handlers, which the rows it draws call by
 * name), currency-symbol, tax-name, tax-rate, vat-rates, tax-exempt, amounts-are.
 *
 * Behaviour, unchanged from when this lived in quote_form.js:
 *   - Opening the section marks the form as using line items and starts with one
 *     blank row. Closing it cancels rows nobody touched, so an auto-added blank
 *     cannot block a submit; rows with real data survive a collapse.
 *   - A reset of the form (a cancelled or completed create) empties the editor,
 *     puts it back on the form's own basis and closes the section.
 * What the rows post and how the server reads them is create_pricing_form.py.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static values = {
        stem: String,
        fnPrefix: String,
        currencySymbol: { type: String, default: '$' },
        taxName: { type: String, default: 'GST' },
        taxRate: { type: String, default: '10' },
        vatRates: String,
        taxExempt: String,
        amountsAre: String,
        holdsPhotos: Boolean,
    };

    connect() {
        this._form = this.element.closest('form');
        // `reset` fires before the browser restores the fields, so the editor
        // settles a task later, once it can read the restored basis.
        this._onReset = () => setTimeout(() => this._reset());
        this._form?.addEventListener('reset', this._onReset);
        this._mount();
    }

    disconnect() {
        this._form?.removeEventListener('reset', this._onReset);
        this.manager = null;
    }

    toggle() {
        const section = this._byId('LineItemsSection');
        if (!section) return;
        if (section.style.display !== 'none') {
            this._close(section);
        } else {
            this._open(section);
        }
    }

    addItem() {
        if (this._mount()) this.manager.add();
    }

    // Chip AI form fill (chip_fill_controller.js) adds a row it read from the
    // user's words: a partial item laid over the blank row. A blank row nobody
    // touched is dropped first; opening after the add marks the form as using
    // line items (reset() cleared that) without adding a blank row. The
    // returned function removes the row again, for the filler's undo.
    fillItem(item) {
        const section = this._byId('LineItemsSection');
        if (!section || !this._mount()) return null;
        if (!this.manager.hasMeaningfulItems()) this.manager.reset();
        const row = this.manager.add({ ...this.manager.blankItem(), ...item });
        this._open(section);
        const id = Number(row.id.slice(this.manager.rowIdPrefix.length));
        return () => this.manager.remove(id);
    }

    // The manager auto-checks the box when rows have content, but a manual
    // toggle must sync the hidden input the form submits from.
    syncShow(event) {
        const hidden = this._byId('ShowLineItems');
        if (hidden) hidden.value = event.target.checked ? 'true' : 'false';
    }

    _open(section) {
        section.style.display = 'block';
        this._toggleEl(section)?.classList.add('expanded');
        this._byId('UseLineItems').value = 'true';
        if (this._mount() && !this.manager.hasItems()) this.manager.add();
    }

    _close(section) {
        section.style.display = 'none';
        this._toggleEl(section)?.classList.remove('expanded');
        if (this.manager && !this.manager.hasMeaningfulItems()) this.manager.reset();
    }

    _reset() {
        const section = this._byId('LineItemsSection');
        if (section) {
            section.style.display = 'none';
            this._toggleEl(section)?.classList.remove('expanded');
        }
        if (this.manager) {
            this.manager.setAmountsAre(this.amountsAreValue);
            this.manager.reset();
        }
        this._badge(0);
    }

    _badge(count) {
        const badge = this._byId('LineItemsBadge');
        if (!badge) return;
        badge.textContent = count;
        badge.style.display = count > 0 ? 'inline' : 'none';
    }

    // The manager script is deferred and may not be there yet when this
    // connects; the first tap mounts it if so.
    _mount() {
        if (this.manager) return this.manager;
        if (typeof window.LineItemsManager === 'undefined') return null;
        this.manager = new window.LineItemsManager({
            containerId: this._id('LineItemsContainer'),
            currencySymbol: this.currencySymbolValue,
            taxName: this.taxNameValue,
            defaultTaxRate: this.taxRateValue,
            vatRates: this.vatRatesValue,
            taxExempt: this.taxExemptValue,
            amountsAre: this.amountsAreValue,
            totalsId: this._id('LineItemsTotals'),
            rowIdPrefix: `${this.stemValue}LineItem_`,
            globalFunctionPrefix: this.fnPrefixValue,
            // The record does not exist yet: rows hold their photo for the form.
            photoOwner: () => (this.holdsPhotosValue ? { type: this.stemValue, holds: true } : null),
            elements: {
                subtotal: this._id('LineItemsSubtotal'),
                total: this._id('LineItemsTotal'),
                amount: this._id('Amount'),
                lineItemsJson: this._id('LineItemsJson'),
                useLineItems: this._id('UseLineItems'),
                showLineItems: this._id('ShowLineItems'),
                showLineItemsCheck: this._id('ShowLineItemsCheck'),
                amountLockedIndicator: this._id('AmountLockedIndicator'),
                amountLockedHelpText: this._id('AmountLockedHelpText'),
                amountHelpText: this._id('AmountHelpText'),
                amountOffsetText: this._id('AmountOffsetText'),
            },
            defaultQuantity: 1,
            onBadgeUpdate: (count) => this._badge(count),
        });
        return this.manager;
    }

    _id(name) {
        return `${this.stemValue}${name}`;
    }

    _byId(name) {
        return document.getElementById(this._id(name));
    }

    _toggleEl(section) {
        return section.previousElementSibling;
    }
}
